/**
 * taxQuestionSnapshotDiff — compare two save-history snapshots of ONE Tax Question V2 version.
 *
 * A snapshot's `payloadJson` is the whole config of one SAVE round, serialized straight off the
 * EF entities:  `{ steps: TaxQuestionStepV2[], items: TaxQuestionItemV2[] }` (camelCase keys).
 * Because the capture side has NO hand-written column list, this diff must not have one either:
 * every key present on either side is compared, so a column added to the table later shows up in
 * the compare dialog on its own, with no edit here.
 *
 * Two ideas carry the whole file:
 *
 *   1. A row's identity is its NATURAL key (`groupCode|itemKind|fieldKey|label` for an item,
 *      `stepCode` for a step) — never its `QuestionItemID`. Ids are stable across a normal save
 *      but a restore/clone can hand the same question a different one, and a question that moves
 *      to another section is genuinely a different row to the admin reading the diff (it comes
 *      out as removed + added, which is the honest reading — see the test file).
 *
 *   2. The audit/identity columns are NOISE. `updatedDate` moves on every single save, so leaving
 *      it in would mark all 84 items "modified" every round and the diff would say nothing.
 *      They are stripped by name (case-insensitively) via IGNORED_FIELDS before anything is
 *      compared.
 *
 * Pure logic — NO Vue/framework import — so it runs under plain Node for the unit tests.
 *   Test:  cd WebAPI/ClientApp && node --test tests/taxQuestionHistoryDiff.test.mjs
 */

/**
 * Columns that must never make a row read as "modified": the row's own identity, the FK to its
 * version, the audit stamps, and the navigation property EF serializes alongside them.
 * Compared case-insensitively — the payload is written with a camelCase policy, but a payload
 * captured before that policy existed (or hand-edited) can still arrive PascalCase.
 */
export const IGNORED_FIELDS = Object.freeze([
  'questionItemID',
  'questionStepID',
  'questionVersionID',
  'createdBy',
  'createdDate',
  'updatedBy',
  'updatedDate',
  'taxQuestionVersionV2',
]);

const IGNORED = new Set(IGNORED_FIELDS.map((f) => f.toLowerCase()));

const EMPTY_PAYLOAD = () => ({ steps: [], items: [] });

/** Case-insensitive property read — exact hit first (the normal path), then a scan. */
function getProp(obj, name) {
  if (!obj) return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, name)) return obj[name];
  const lname = String(name).toLowerCase();
  const hit = Object.keys(obj).find((k) => k.toLowerCase() === lname);
  return hit === undefined ? undefined : obj[hit];
}

/** null/undefined → '' · object/array → JSON · everything else → String(). */
function normalizeValue(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

/** The key-building form of a value: trimmed + lowercased, so casing/padding is not an identity. */
function keyPart(v) {
  return normalizeValue(v).trim().toLowerCase();
}

/** Only real records survive — a null / string / array element is not a row. */
function rowsOf(container, name) {
  const value = getProp(container, name);
  if (!Array.isArray(value)) return [];
  return value.filter((r) => r && typeof r === 'object' && !Array.isArray(r));
}

/**
 * `payloadJson` → `{ steps, items }`. Accepts the raw string off the API, an already-parsed
 * object, null/'' , or complete garbage — a broken payload yields an empty diff, never a throw,
 * because this runs inside a dialog the admin opened to LOOK at history.
 */
export function parseSnapshotPayload(payloadJson) {
  let raw = payloadJson;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return EMPTY_PAYLOAD();
    try {
      raw = JSON.parse(trimmed);
    } catch {
      return EMPTY_PAYLOAD();
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return EMPTY_PAYLOAD();
  return { steps: rowsOf(raw, 'steps'), items: rowsOf(raw, 'items') };
}

/** Natural key of an item: what it is + where it lives + what it binds, then its label. */
export function snapshotItemKey(item) {
  return [
    keyPart(getProp(item, 'groupCode')),
    keyPart(getProp(item, 'itemKind')),
    keyPart(getProp(item, 'fieldKey')),
    keyPart(getProp(item, 'label')),
  ].join('|');
}

/** Natural key of a step: its StepCode (unique per version, and the items' GroupCode). */
export function snapshotStepKey(step) {
  return keyPart(getProp(step, 'stepCode'));
}

/**
 * Display form of a value, cut at `max` with an ellipsis — for the JSON blobs
 * (`cardConfigJson`, `summaryTemplateJson`, …) which are compared whole but must not be
 * rendered whole.
 */
export function truncateValue(v, max = 300) {
  const s = normalizeValue(v);
  if (!(max > 0)) return '';
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/**
 * Union of the comparable keys of both rows, as `lowercase → the casing to display`.
 * `first` is consulted first so the current (right) side decides the casing and the field order.
 */
function comparableKeys(first, second) {
  const seen = new Map();
  [first, second].forEach((row) => {
    if (!row) return;
    Object.keys(row).forEach((k) => {
      const lk = k.toLowerCase();
      if (IGNORED.has(lk) || seen.has(lk)) return;
      seen.set(lk, k);
    });
  });
  return seen;
}

/** Only the fields that actually differ — an unchanged row therefore reports `fields: []`. */
function changedFields(left, right) {
  const out = [];
  comparableKeys(right, left).forEach((display, lk) => {
    const lv = normalizeValue(getProp(left, lk));
    const rv = normalizeValue(getProp(right, lk));
    if (lv !== rv) out.push({ name: display, left: lv, right: rv });
  });
  return out;
}

/** For an added/removed row: everything it carries, so the panel can show what appeared/vanished. */
function presentFields(row, side) {
  const out = [];
  comparableKeys(row, null).forEach((display, lk) => {
    const v = normalizeValue(getProp(row, lk));
    if (v === '') return;
    out.push(side === 'right' ? { name: display, left: '', right: v } : { name: display, left: v, right: '' });
  });
  return out;
}

/**
 * Index rows by natural key, keeping insertion order. A repeated key gets `#1`, `#2`, … appended
 * (the FIRST occurrence keeps the bare key, so one-on-one still matches when only one side
 * duplicates). FieldKey has no unique index, and two label-less items in the same section collide
 * on purpose — without this they would collapse into one another and silently disappear.
 */
function indexByKey(rows, keyFn) {
  const map = new Map();
  const order = [];
  (rows || []).forEach((row) => {
    const base = keyFn(row);
    let key = base;
    let n = 0;
    while (map.has(key)) {
      n += 1;
      key = `${base}#${n}`;
    }
    map.set(key, row);
    order.push(key);
  });
  return { map, order };
}

function itemMeta(row) {
  const label = normalizeValue(getProp(row, 'label'));
  return {
    label: label || normalizeValue(getProp(row, 'questionTextTh')) || normalizeValue(getProp(row, 'fieldKey')),
    groupCode: normalizeValue(getProp(row, 'groupCode')),
    itemKind: normalizeValue(getProp(row, 'itemKind')),
    fieldKey: normalizeValue(getProp(row, 'fieldKey')),
  };
}

function stepMeta(row) {
  const stepCode = normalizeValue(getProp(row, 'stepCode'));
  return { label: normalizeValue(getProp(row, 'label')) || stepCode, stepCode };
}

/** Right (current) order first, then the rows that only the left (older) side had. */
function diffRows(leftRows, rightRows, keyFn, metaFn) {
  const L = indexByKey(leftRows, keyFn);
  const R = indexByKey(rightRows, keyFn);
  const out = [];

  R.order.forEach((key) => {
    const right = R.map.get(key);
    if (!L.map.has(key)) {
      out.push({ key, state: 'added', ...metaFn(right), fields: presentFields(right, 'right') });
      return;
    }
    const fields = changedFields(L.map.get(key), right);
    out.push({ key, state: fields.length ? 'modified' : 'unchanged', ...metaFn(right), fields });
  });

  L.order.forEach((key) => {
    if (R.map.has(key)) return;
    const left = L.map.get(key);
    out.push({ key, state: 'removed', ...metaFn(left), fields: presentFields(left, 'left') });
  });

  return out;
}

/**
 * Compare two snapshot payloads (older on the left, newer on the right).
 *
 * → `{ summary: { added, removed, modified, unchanged },   // items + steps together
 *      items: [ { key, state, label, groupCode, itemKind, fieldKey, fields } ],
 *      steps: [ { key, state, label, stepCode, fields } ] }`
 * where `fields` is `[ { name, left, right } ]` and is `[]` on an unchanged row.
 */
export function diffSnapshots(leftPayloadJson, rightPayloadJson) {
  const left = parseSnapshotPayload(leftPayloadJson);
  const right = parseSnapshotPayload(rightPayloadJson);

  const items = diffRows(left.items, right.items, snapshotItemKey, itemMeta);
  const steps = diffRows(left.steps, right.steps, snapshotStepKey, stepMeta);

  const summary = { added: 0, removed: 0, modified: 0, unchanged: 0 };
  items.concat(steps).forEach((row) => {
    summary[row.state] += 1;
  });

  return { summary, items, steps };
}
